import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Stack,
    Container,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
// Uebersetzungen inline — die Seite ist public/standalone (noLayout)
// und haengt bewusst nicht am i18nProvider (2026-09 aus dem Admin
// hierher gezogen, azubi:web-Feedback: jede App haendelt ihre
// Einladungen selbst).
const deRegistration = {
        title: 'Registrierung abschließen',
        company: 'Firma',
        email: 'E-Mail',
        assignedRoles: 'Zugewiesene Rollen',
        fields: {
            firstName: 'Vorname',
            lastName: 'Nachname',
            password: 'Passwort',
            confirmPassword: 'Passwort bestätigen',
            passwordHelp: 'Mindestens 10 Zeichen',
            phone: 'Telefon',
            mobile: 'Mobil',
            street: 'Straße',
            zip: 'PLZ',
            city: 'Stadt',
            country: 'Land',
            jobTitle: 'Position',
            department: 'Abteilung',
            none: 'Keine',
        },
        buttons: {
            submit: 'Registrierung abschließen',
            goToLogin: 'Zur Anmeldung',
        },
        errors: {
            noToken: 'Kein Einladungs-Token angegeben.',
            invalidToken: 'Ungültige oder abgelaufene Einladung.',
            validationFailed: 'Einladung konnte nicht überprüft werden. Bitte versuchen Sie es erneut.',
            passwordMismatch: 'Die Passwörter stimmen nicht überein.',
            passwordTooShort: 'Das Passwort muss mindestens 10 Zeichen lang sein.',
            registrationFailed: 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
            networkError: 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
        },
        success: {
            title: 'Registrierung erfolgreich!',
            message: 'Ihr Konto wurde erfolgreich erstellt. Sie werden zur Anmeldung weitergeleitet...',
        },
        error: {
            title: 'Registrierungsfehler',
        },
    };

const enRegistration = {
        title: 'Complete Your Registration',
        company: 'Company',
        email: 'Email',
        assignedRoles: 'Assigned Roles',
        fields: {
            firstName: 'First Name',
            lastName: 'Last Name',
            password: 'Password',
            confirmPassword: 'Confirm Password',
            passwordHelp: 'Minimum 10 characters',
            phone: 'Phone',
            mobile: 'Mobile',
            street: 'Street',
            zip: 'ZIP',
            city: 'City',
            country: 'Country',
            jobTitle: 'Job Title',
            department: 'Department',
            none: 'None',
        },
        buttons: {
            submit: 'Complete Registration',
            goToLogin: 'Go to Login',
        },
        errors: {
            noToken: 'No invitation token provided.',
            invalidToken: 'Invalid or expired invitation.',
            validationFailed: 'Failed to validate invitation. Please try again.',
            passwordMismatch: 'Passwords do not match.',
            passwordTooShort: 'Password must be at least 10 characters long.',
            registrationFailed: 'Registration failed. Please try again.',
            networkError: 'Failed to register. Please try again.',
        },
        success: {
            title: 'Registration Successful!',
            message: 'Your account has been created successfully. You will be redirected to the login page...',
        },
        error: {
            title: 'Registration Error',
        },
    };

interface InvitationData {
    valid: boolean;
    email?: string;
    company?: {
        id: number;
        name: string;
    };
    roles?: Array<{ id: number; name: string }>;
    expiresAt?: string;
}

interface Department {
    id: number;
    name: string;
}

// Simple translation helper for standalone page
const getTranslations = () => {
    const browserLang = navigator.language.split('-')[0];
    return browserLang === 'de' ? deRegistration : enRegistration;
};

const RegisterPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const t = useMemo(() => getTranslations(), []);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [invitation, setInvitation] = useState<InvitationData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [isAdmin, setIsAdmin] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
        phone: '',
        mobile: '',
        street: '',
        zip: '',
        city: '',
        country: '',
        jobTitle: '',
        departmentId: '',
        language: 'de',
    });

    useEffect(() => {
        if (!token) {
            setError(t.errors.noToken);
            setLoading(false);
            return;
        }

        const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';

        // Validate token and fetch departments in parallel
        Promise.all([
            fetch(`${ssoUrl}/api/invitations/validate/${token}`).then(r => r.json()),
            fetch(`${ssoUrl}/api/invitations/${token}/departments`).then(r => r.json()),
        ])
            .then(([validationData, deptData]) => {
                if (validationData.valid) {
                    setInvitation(validationData);
                    setDepartments(deptData.departments || []);
                    setIsAdmin(deptData.isAdmin || false);
                } else {
                    setError(validationData.message || t.errors.invalidToken);
                }
            })
            .catch((err) => {
                console.error('Validation error:', err);
                setError(t.errors.validationFailed);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [token, t]);

    const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [field]: event.target.value,
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        // Validate passwords
        if (formData.password !== formData.confirmPassword) {
            setError(t.errors.passwordMismatch);
            return;
        }

        if (formData.password.length < 10) {
            setError(t.errors.passwordTooShort);
            return;
        }

        setSubmitting(true);

        const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';

        try {
            const response = await fetch(`${ssoUrl}/api/invitations/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    ...formData,
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSuccess(true);
                setTimeout(() => {
                    window.location.href = '/';
                }, 3000);
            } else {
                setError(data.error || t.errors.registrationFailed);
            }
        } catch (err) {
            setError(t.errors.networkError);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !invitation) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ mt: 8 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" component="h1" gutterBottom>
                                {t.error.title}
                            </Typography>
                            <Alert severity="error">{error}</Alert>
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={() => { window.location.href = '/'; }}
                            >
                                {t.buttons.goToLogin}
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Container>
        );
    }

    if (success) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ mt: 8 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h5" component="h1" gutterBottom>
                                {t.success.title}
                            </Typography>
                            <Alert severity="success">
                                {t.success.message}
                            </Alert>
                        </CardContent>
                    </Card>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h4" component="h1" gutterBottom align="center">
                            {t.title}
                        </Typography>

                        {invitation && (
                            <Box sx={{ mb: 3 }}>
                                <Alert severity="info">
                                    <Typography variant="body2">
                                        <strong>{t.company}:</strong> {invitation.company?.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>{t.email}:</strong> {invitation.email}
                                    </Typography>
                                    {invitation.roles && invitation.roles.length > 0 && (
                                        <Typography variant="body2">
                                            <strong>{t.assignedRoles}:</strong> {invitation.roles.map(r => r.name).join(', ')}
                                        </Typography>
                                    )}
                                </Alert>
                            </Box>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={2}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        fullWidth
                                        label={t.fields.firstName}
                                        required
                                        value={formData.firstName}
                                        onChange={handleChange('firstName')}
                                    />
                                    <TextField
                                        fullWidth
                                        label={t.fields.lastName}
                                        required
                                        value={formData.lastName}
                                        onChange={handleChange('lastName')}
                                    />
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        fullWidth
                                        label={t.fields.password}
                                        type="password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange('password')}
                                        helperText={t.fields.passwordHelp}
                                    />
                                    <TextField
                                        fullWidth
                                        label={t.fields.confirmPassword}
                                        type="password"
                                        required
                                        value={formData.confirmPassword}
                                        onChange={handleChange('confirmPassword')}
                                    />
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        fullWidth
                                        label={t.fields.phone}
                                        value={formData.phone}
                                        onChange={handleChange('phone')}
                                    />
                                    <TextField
                                        fullWidth
                                        label={t.fields.mobile}
                                        value={formData.mobile}
                                        onChange={handleChange('mobile')}
                                    />
                                </Stack>
                                <TextField
                                    fullWidth
                                    label={t.fields.street}
                                    value={formData.street}
                                    onChange={handleChange('street')}
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        label={t.fields.zip}
                                        value={formData.zip}
                                        onChange={handleChange('zip')}
                                        sx={{ width: { xs: '100%', sm: '30%' } }}
                                    />
                                    <TextField
                                        fullWidth
                                        label={t.fields.city}
                                        value={formData.city}
                                        onChange={handleChange('city')}
                                    />
                                </Stack>
                                <TextField
                                    fullWidth
                                    label={t.fields.country}
                                    value={formData.country}
                                    onChange={handleChange('country')}
                                />
                                <TextField
                                    fullWidth
                                    label={t.fields.jobTitle}
                                    value={formData.jobTitle}
                                    onChange={handleChange('jobTitle')}
                                />
                                {isAdmin && departments.length > 0 && (
                                    <FormControl fullWidth>
                                        <InputLabel>{t.fields.department}</InputLabel>
                                        <Select
                                            value={formData.departmentId}
                                            label={t.fields.department}
                                            onChange={(e) => setFormData({ ...formData, departmentId: e.target.value as string })}
                                        >
                                            <MenuItem value="">
                                                <em>{t.fields.none}</em>
                                            </MenuItem>
                                            {departments.map((dept) => (
                                                <MenuItem key={dept.id} value={dept.id}>
                                                    {dept.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={submitting}
                                >
                                    {submitting ? <CircularProgress size={24} /> : t.buttons.submit}
                                </Button>
                            </Stack>
                        </form>
                    </CardContent>
                </Card>
            </Box>
        </Container>
    );
};

export default RegisterPage;
